import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { useMediaTicket, mediaUrl } from "@/lib/mediaTicket";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionLoader } from "@/components/common";
import { SocialPostDialog } from "@/components/social/SocialPostGenerator";
import ReplayChapters from "@/components/ReplayChapters";
import { useStreamPlayer } from "@/hooks/useStreamPlayer";
import { useHomepageReplay, SessionReplayToggle } from "@/components/hub/HomepageReplayControls";
import {
  ArrowLeft, Download, Loader2, Sparkles, FileText, Wand2, Plus, X, Quote,
  ListChecks, Tags, Users2, AlertTriangle, Search, Scissors, Megaphone, RefreshCw, Radio, ListOrdered,
} from "lucide-react";

const SPEAKER_STYLES = [
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-orange-100 text-orange-800 border-orange-200",
  "bg-violet-100 text-violet-800 border-violet-200",
  "bg-sky-100 text-sky-800 border-sky-200",
  "bg-rose-100 text-rose-800 border-rose-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
];

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function Highlighted({ text, phrases }) {
  const list = (phrases || []).filter((p) => p && p.trim());
  if (!list.length) return <>{text}</>;
  const re = new RegExp(`(${list.map(escapeRe).join("|")})`, "ig");
  const parts = text.split(re);
  return (
    <>
      {parts.map((part, i) => {
        const isMatch = list.some((p) => p.toLowerCase() === part.toLowerCase());
        return isMatch ? <mark key={`p-${i}`} className="rounded bg-amber-200/80 px-0.5 text-foreground">{part}</mark> : <span key={`p-${i}`}>{part}</span>;
      })}
    </>
  );
}

export default function RecordingDetail() {
  const ticket = useMediaTicket();
  const { id } = useParams();
  const navigate = useNavigate();
  const homepageReplay = useHomepageReplay();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [members, setMembers] = useState([]);
  const [starting, setStarting] = useState("");
  const [phrases, setPhrases] = useState([]);
  const [phraseInput, setPhraseInput] = useState("");
  const [speakerMap, setSpeakerMap] = useState({});
  const [keyterms, setKeyterms] = useState([]);
  const [keytermInput, setKeytermInput] = useState("");
  const prevStatus = useRef(null);
  const [dl, setDl] = useState(null);
  const { iframeRef: cfRef, seek: cfSeek, currentTime: cfTime } = useStreamPlayer(rec?.cf_iframe);
  const [chapBusy, setChapBusy] = useState(false);

  const generateChapters = async () => {
    setChapBusy(true);
    try {
      const r = await api.post(`/recordings/${id}/chapters/generate`);
      setRec((x) => ({ ...x, chapters_status: r.data.chapters_status }));
      toast.success("Building chapters from the transcript…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start chapters"); }
    finally { setChapBusy(false); }
  };

  // Cloudflare recordings: kick MP4 generation as soon as the page opens and show progress on the button.
  useEffect(() => {
    if (!rec || rec.source !== "cloudflare" || !rec.has_audio) return undefined;
    let active = true;
    let timer = null;
    const tick = async () => {
      try {
        const r = await api.get(`/recordings/${id}/download-status`);
        if (!active) return;
        setDl(r.data);
        if (r.data.status !== "ready" && r.data.status !== "error") timer = setTimeout(tick, 10000);
      } catch (e) { /* non-fatal */ }
    };
    tick();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [rec?.source, rec?.has_audio, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/recordings/${id}`);
      setRec(r.data);
      setPhrases(r.data.watch_phrases || []);
      setSpeakerMap(r.data.speaker_map || {});
      setKeyterms(r.data.keyterms || []);
    } catch (e) {
      toast.error("Recording not found");
      navigate("/admin/studio/recordings");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  // Poll while a background job (finalizing/transcription/analysis/chapters) is running.
  useEffect(() => {
    const pending = rec?.status === "processing" || rec?.status === "transcribing" || rec?.status === "analyzing" || rec?.chapters_status === "generating";
    if (!pending) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/recordings/${id}`);
        setRec(r.data);
      } catch (e) { console.debug("recording status poll failed, will retry", e); }
    }, 4000);
    return () => clearInterval(t);
  }, [rec?.status, rec?.chapters_status, id]);

  // Surface completion / failure as toasts when a background job settles.
  useEffect(() => {
    if (!rec) return;
    const s = rec.status;
    const prev = prevStatus.current;
    if (prev === "transcribing" && s !== "transcribing") {
      if (s === "transcribe_failed") toast.error(rec.transcribe_error ? `Transcription failed: ${rec.transcribe_error}` : "Transcription failed");
      else { setSpeakerMap(rec.speaker_map || {}); toast.success(rec.is_sandbox ? "Transcribed (sandbox)" : "Transcribed"); }
    }
    if (prev === "analyzing" && s !== "analyzing") {
      if (s === "analyze_failed") toast.error(rec.analyze_error ? `Analysis failed: ${rec.analyze_error}` : "Analysis failed");
      else if (s === "analyzed") toast.success("Analysis complete");
    }
    prevStatus.current = s;
  }, [rec?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const transcribe = async () => {
    setStarting("transcribe");
    try {
      const r = await api.post(`/recordings/${id}/transcribe`);
      setRec(r.data);
      if (r.data.status === "transcribing") toast.info("Transcription started — this can take a few minutes for longer audio.");
      else setSpeakerMap(r.data.speaker_map || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start transcription"); }
    finally { setStarting(""); }
  };

  const analyze = async () => {
    setStarting("analyze");
    try {
      const r = await api.post(`/recordings/${id}/analyze`, { watch_phrases: phrases });
      setRec(r.data);
      if (r.data.status === "analyzing") toast.info("Analyzing with AI…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start analysis"); }
    finally { setStarting(""); }
  };

  const resetRetry = async () => {
    setStarting("retry");
    try {
      const r = await api.post(`/recordings/${id}/retry`);
      setRec(r.data);
      toast.info("Re-processing from the saved audio…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restart processing"); }
    finally { setStarting(""); }
  };

  const saveSpeakers = async (map) => {
    setSpeakerMap(map);
    try { const r = await api.put(`/recordings/${id}`, { speaker_map: map }); setRec(r.data); }
    catch (e) { toast.error("Could not save speakers"); }
  };

  const saveKeyterms = async (list) => {
    setKeyterms(list);
    try { const r = await api.put(`/recordings/${id}`, { keyterms: list }); setRec(r.data); }
    catch (e) { toast.error("Could not save vocabulary"); }
  };

  const addKeyterm = () => {
    const v = keytermInput.trim();
    if (v && !keyterms.includes(v)) saveKeyterms([...keyterms, v]);
    setKeytermInput("");
  };

  const addDirectoryNames = async () => {
    try {
      const r = await api.get("/networking/directory");
      const set = new Set(keyterms);
      (r.data || []).forEach((e) => {
        if (e.name) set.add(e.name);
        if (e.business?.business_name) set.add(e.business.business_name);
      });
      const list = [...set].slice(0, 200);
      await saveKeyterms(list);
      toast.success(`Vocabulary updated — ${list.length} term${list.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error("Could not load directory names"); }
  };

  const applyAiSpeakers = () => {
    const map = { ...speakerMap };
    (rec?.analysis?.speakers || []).forEach((s) => {
      const label = (s.label || "").replace(/^Speaker\s+/i, "").trim();
      if (label && s.guessed_name) map[label] = { name: s.guessed_name, user_id: map[label]?.user_id || null };
    });
    saveSpeakers(map);
    toast.success("Applied AI speaker guesses");
  };

  const addPhrase = () => {
    const v = phraseInput.trim();
    if (v && !phrases.includes(v)) setPhrases([...phrases, v]);
    setPhraseInput("");
  };

  if (loading) return <SectionLoader />;
  if (!rec) return null;

  const utts = rec.utterances || [];
  const labels = [...new Set(utts.map((u) => u.speaker))];
  const labelColor = (label) => SPEAKER_STYLES[labels.indexOf(label) % SPEAKER_STYLES.length];
  const displayName = (label) => speakerMap[label]?.name || (label && label.length <= 2 ? `Speaker ${label}` : label);
  const a = rec.analysis;
  const transcribing = rec.status === "transcribing";
  const analyzing = rec.status === "analyzing";
  const replaySession = rec.show_id ? (homepageReplay.state?.recent || []).find((s) => s.show_id === rec.show_id) : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/admin/studio/recordings" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All recordings</Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="font-serif text-2xl font-semibold text-foreground">{rec.title}</h1>
            {rec.is_sandbox && <Badge className="border border-amber-200 bg-amber-50 text-amber-800">Sandbox transcript</Badge>}
            {rec.transcription_provider && <Badge variant="secondary">{rec.transcription_provider}</Badge>}
          </div>
          {rec.source === "cloudflare" && rec.cf_uid && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-testid="rec-visibility">
              <span className="text-muted-foreground">Who can watch the replay:</span>
              <Select value={rec.visibility === "public" ? "public" : "members"} onValueChange={async (v) => { try { const r = await api.put(`/recordings/${id}`, { visibility: v }); setRec(r.data); toast.success(v === "public" ? "Replay is public — anyone can watch it on the homepage" : "Replay is members-only — visitors see a sign-in card"); } catch (e) { toast.error("Could not update"); } }}>
                <SelectTrigger className="h-8 w-[190px]" data-testid="rec-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="members">Members only</SelectItem>
                  <SelectItem value="public">Public — anyone</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {replaySession && (
            <div className="mt-2 max-w-xl">
              <SessionReplayToggle visible={replaySession.visible} globalEnabled={homepageReplay.state?.enabled} onChange={(v) => homepageReplay.setSessionVisible(replaySession.session_id, v)} testId="rec-replay-homepage-toggle" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowPost(true)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="rec-createpost-button">
            <Megaphone className="mr-2 h-4 w-4" />Create post
          </Button>
          {rec.has_audio && rec.source !== "cloudflare" && (
            <Button asChild variant="secondary" data-testid="rec-clip-editor-button">
              <Link to={`/admin/recordings/${id}/clips`}><Scissors className="mr-2 h-4 w-4" />Clip editor</Link>
            </Button>
          )}
          {rec.has_audio && (
            <Button asChild variant="secondary" data-testid="rec-download-button" title={dl && dl.status !== "ready" ? "Cloudflare is building the MP4 — click to open a page that starts the download when it's ready" : "Download"}>
              <a href={mediaUrl(`${API_BASE}/recordings/${id}/audio`, ticket) || "#"} data-download-status={dl?.status || "ready"}>
                {rec.source === "cloudflare" && dl && dl.status === "inprogress" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing MP4… {Math.round(dl.percent || 0)}%</>
                  : rec.source === "cloudflare" && dl && dl.status === "error" ? <><AlertTriangle className="mr-2 h-4 w-4" />MP4 failed</>
                  : <><Download className="mr-2 h-4 w-4" />Download{rec.source === "cloudflare" ? " MP4" : ""}</>}
              </a>
            </Button>
          )}
        </div>
      </div>

      {rec.is_sandbox && (
        <Card className="border-amber-200 bg-amber-50/60"><CardContent className="flex items-start gap-3 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
          <div className="text-sm text-amber-900">This is an older <strong>sandbox transcript</strong>. Real transcription is now connected — click <strong>Re-transcribe</strong> above to regenerate a real diarized transcript with AssemblyAI.</div>
        </CardContent></Card>
      )}

      {rec.has_audio && (
        <Card className="border-border bg-card"><CardContent className="p-4">
          {rec.source === "cloudflare" && rec.cf_iframe ? (
            <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
              <iframe
                ref={cfRef}
                src={rec.cf_iframe}
                title={rec.title}
                className="h-full w-full"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                data-testid="rec-cf-player"
              />
            </div>
          ) : ((rec.audio_mime || "").startsWith("video/") || rec.has_video) ? (
            <video
              controls
              playsInline
              className="max-h-[60vh] w-full rounded-lg bg-black"
              src={mediaUrl(`${API_BASE}/recordings/${id}/audio`, ticket) || undefined}
              data-testid="rec-video-player"
            />
          ) : (
            <audio controls className="w-full" src={mediaUrl(`${API_BASE}/recordings/${id}/audio`, ticket) || undefined} data-testid="rec-audio-player" />
          )}
        </CardContent></Card>
      )}

      {/* Chapters — clickable markers that also show under the homepage replay */}
      {rec.has_audio && (rec.chapters?.length > 0 || (rec.utterances || []).some((u) => (u.start || 0) > 0)) && (
        <Card className="border-border bg-card" data-testid="rec-chapters-card"><CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="flex items-center gap-2 font-medium text-foreground"><ListOrdered className="h-4 w-4 text-primary" />Chapters</h3>
              <p className="text-xs text-muted-foreground">{rec.chapters?.length ? "Click one to jump the player. These appear under the replay on the homepage." : "Turn the transcript's topics into jump-to markers for the replay."}</p>
            </div>
            <Button size="sm" variant="secondary" onClick={generateChapters} disabled={chapBusy || rec.chapters_status === "generating"} data-testid="rec-chapters-generate">
              {rec.chapters_status === "generating" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Building…</> : <><Wand2 className="mr-2 h-4 w-4" />{rec.chapters?.length ? "Regenerate" : "Generate chapters"}</>}
            </Button>
          </div>
          {rec.chapters_status === "failed" && rec.chapters_error && <p className="text-xs text-red-600" data-testid="rec-chapters-error">{rec.chapters_error}</p>}
          {rec.chapters?.length > 0 && <ReplayChapters chapters={rec.chapters} currentTime={cfTime} onSeek={rec.source === "cloudflare" ? cfSeek : undefined} testPrefix="rec-chapter" />}
        </CardContent></Card>
      )}

      {/* Action bar */}
      <Card className="border-border bg-card"><CardContent className="flex flex-wrap items-center gap-2 p-3">
        {rec.has_audio && (
          <Button size="sm" variant="secondary" onClick={transcribe} disabled={transcribing || analyzing || starting === "transcribe"} data-testid="rec-transcribe-button">
            {(transcribing || starting === "transcribe") ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            {transcribing ? "Transcribing…" : (utts.length ? "Re-transcribe" : "Transcribe")}
          </Button>
        )}
        <Button size="sm" onClick={analyze} disabled={analyzing || transcribing || starting === "analyze" || !utts.length} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="rec-analyze-button">
          {(analyzing || starting === "analyze") ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
          {analyzing ? "Analyzing…" : (a ? "Re-analyze" : "Analyze with AI")}
        </Button>
        {!utts.length && !transcribing && <span className="text-xs text-muted-foreground">Transcribe (or paste) first, then analyze.</span>}
      </CardContent></Card>

      {/* Background job status */}
      {rec.status === "processing" && (
        <Card className="border-primary/30 bg-primary/5" data-testid="rec-processing-banner"><CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
          <div className="text-sm text-foreground">Finalizing your recording (saving a durable copy). This only takes a moment — it'll be ready to play and download shortly.</div>
        </CardContent></Card>
      )}
      {rec.status === "imported" && (
        <Card className="border-primary/30 bg-primary/5" data-testid="rec-imported-banner"><CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Radio className="h-5 w-5 shrink-0 text-primary" />
          <div className="text-sm text-foreground">Imported from your live broadcast (Cloudflare). Click <strong>Transcribe</strong> to generate a diarized transcript and AI insights.</div>
        </CardContent></Card>
      )}
      {transcribing && (
        <Card className="border-primary/30 bg-primary/5" data-testid="rec-transcribing-banner"><CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
          <div className="text-sm text-foreground">Transcribing your audio with AssemblyAI (diarized speaker labels). This can take a few minutes for longer recordings — you can leave this page and come back; it keeps running.</div>
          <Button size="sm" variant="outline" className="ml-auto" onClick={resetRetry} disabled={starting === "retry"} data-testid="rec-reset-retry">
            {starting === "retry" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Reset &amp; retry
          </Button>
        </CardContent></Card>
      )}
      {analyzing && (
        <Card className="border-primary/30 bg-primary/5" data-testid="rec-analyzing-banner"><CardContent className="flex items-center gap-3 p-4">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
          <div className="text-sm text-foreground">Analyzing the transcript with AI…</div>
        </CardContent></Card>
      )}
      {rec.status === "transcribe_failed" && (
        <Card className="border-destructive/40 bg-destructive/5" data-testid="rec-transcribe-failed-banner"><CardContent className="flex flex-wrap items-center gap-3 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div className="text-sm text-foreground">Transcription failed{rec.transcribe_error ? `: ${rec.transcribe_error}` : "."}</div>
          <Button size="sm" variant="secondary" className="ml-auto" onClick={transcribe} data-testid="rec-transcribe-retry"><FileText className="mr-2 h-4 w-4" />Retry</Button>
        </CardContent></Card>
      )}
      {rec.status === "analyze_failed" && (
        <Card className="border-destructive/40 bg-destructive/5" data-testid="rec-analyze-failed-banner"><CardContent className="flex flex-wrap items-center gap-3 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div className="text-sm text-foreground">Analysis failed{rec.analyze_error ? `: ${rec.analyze_error}` : "."}</div>
          <Button size="sm" variant="secondary" className="ml-auto" onClick={analyze} data-testid="rec-analyze-retry"><Wand2 className="mr-2 h-4 w-4" />Retry</Button>
        </CardContent></Card>
      )}

      {/* Vocabulary boost (keyterms_prompt) */}
      {rec.has_audio && (
        <Card className="border-border bg-card"><CardContent className="p-4">
          <Label className="flex items-center gap-1.5 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" />Boost recognition (vocabulary)</Label>
          <p className="text-xs text-muted-foreground">Add names, companies and terms so AssemblyAI spells them correctly. Applied when you (re-)transcribe. Up to 200 terms.</p>
          <div className="mt-2 flex gap-2">
            <Input value={keytermInput} onChange={(e) => setKeytermInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyterm(); } }} placeholder="e.g. WannaWare, Spokane, Chris Durkin" data-testid="rec-keyterm-input" />
            <Button type="button" variant="secondary" onClick={addKeyterm} data-testid="rec-keyterm-add"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {keyterms.map((k) => (
              <Badge key={k} variant="secondary" className="gap-1" data-testid="rec-keyterm-badge">{k}<button onClick={() => saveKeyterms(keyterms.filter((x) => x !== k))} aria-label={`Remove ${k}`}><X className="h-3 w-3" /></button></Badge>
            ))}
            {keyterms.length === 0 && <span className="text-xs text-muted-foreground">No terms yet.</span>}
          </div>
          <Button size="sm" variant="ghost" className="mt-2 h-7 text-xs" onClick={addDirectoryNames} data-testid="rec-add-directory-names"><Users2 className="mr-1 h-3.5 w-3.5" />Add member &amp; business names</Button>
        </CardContent></Card>
      )}

      {/* Watch phrases */}
      <Card className="border-border bg-card"><CardContent className="p-4">
        <Label className="flex items-center gap-1.5 text-sm font-semibold"><Search className="h-4 w-4" />Talking points / watch phrases</Label>
        <p className="text-xs text-muted-foreground">The AI flags and highlights these in the transcript.</p>
        <div className="mt-2 flex gap-2">
          <Input value={phraseInput} onChange={(e) => setPhraseInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPhrase(); } }} placeholder="e.g. referral, follow-up, partnership" data-testid="rec-phrase-input" />
          <Button type="button" variant="secondary" onClick={addPhrase}><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {phrases.map((p) => <Badge key={p} variant="secondary" className="gap-1">{p}<button onClick={() => setPhrases(phrases.filter((x) => x !== p))}><X className="h-3 w-3" /></button></Badge>)}
          {phrases.length === 0 && <span className="text-xs text-muted-foreground">No phrases yet.</span>}
        </div>
      </CardContent></Card>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Transcript */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 font-serif text-lg font-semibold text-foreground"><FileText className="h-4 w-4" />Transcript</h2>
          </div>

          {labels.length > 0 && (
            <Card className="border-border bg-card"><CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Speakers</p>
                {a?.speakers?.length > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={applyAiSpeakers} data-testid="rec-apply-speakers"><Sparkles className="mr-1 h-3.5 w-3.5" />Apply AI guesses</Button>}
              </div>
              <div className="mt-2 space-y-2">
                {labels.map((label) => (
                  <div key={label} className="flex items-center gap-2">
                    <Badge variant="outline" className={`shrink-0 ${labelColor(label)}`}>{label && label.length <= 2 ? `Speaker ${label}` : label}</Badge>
                    <Input className="h-8" placeholder="Assign a name" value={speakerMap[label]?.name || ""} onChange={(e) => setSpeakerMap({ ...speakerMap, [label]: { ...speakerMap[label], name: e.target.value } })} onBlur={() => saveSpeakers(speakerMap)} data-testid="rec-speaker-name" />
                    <Select value={speakerMap[label]?.user_id || "none"} onValueChange={(v) => { const m = members.find((x) => x.user_id === v); saveSpeakers({ ...speakerMap, [label]: { name: v === "none" ? speakerMap[label]?.name || "" : (m?.name || ""), user_id: v === "none" ? null : v } }); }}>
                      <SelectTrigger className="h-8 w-40"><SelectValue placeholder="Link member" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No member</SelectItem>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}

          <Card className="border-border bg-card"><CardContent className="space-y-3 p-4" data-testid="rec-transcript-body">
            {utts.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No transcript yet. {rec.has_audio ? "Click Transcribe." : "Create with a pasted transcript or upload audio."}</p>
            ) : (
              utts.map((u, i) => (
                <div key={u.start != null ? `${u.start}-${u.speaker ?? ""}` : i} className="flex gap-3">
                  <Badge variant="outline" className={`mt-0.5 h-fit shrink-0 ${labelColor(u.speaker)}`}>{displayName(u.speaker)}</Badge>
                  <p className="text-[15px] leading-7 text-foreground"><Highlighted text={u.text} phrases={phrases} /></p>
                </div>
              ))
            )}
          </CardContent></Card>
        </div>

        {/* Insights */}
        <div className="space-y-3">
          <h2 className="flex items-center gap-1.5 font-serif text-lg font-semibold text-foreground"><Sparkles className="h-4 w-4 text-primary" />AI Insights</h2>
          {!a ? (
            <Card className="border-dashed bg-card"><CardContent className="p-8 text-center">
              <Wand2 className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Run <strong>Analyze with AI</strong> to generate a summary, key topics, action items, highlights and watch-phrase matches.</p>
            </CardContent></Card>
          ) : (
            <>
              <Card className="border-border bg-card"><CardContent className="p-4"><p className="text-xs font-semibold uppercase tracking-wider text-primary">Summary</p><p className="mt-2 text-sm leading-6 text-foreground" data-testid="rec-summary">{a.summary}</p></CardContent></Card>

              {a.topics?.length > 0 && (
                <Card className="border-border bg-card"><CardContent className="p-4"><p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Tags className="h-4 w-4" />Topics</p><div className="mt-2 flex flex-wrap gap-1.5">{a.topics.map((t, i) => <Badge key={`${t}-${i}`} variant="secondary">{t}</Badge>)}</div></CardContent></Card>
              )}

              {a.action_items?.length > 0 && (
                <Card className="border-border bg-card"><CardContent className="p-4"><p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><ListChecks className="h-4 w-4" />Action items</p><ul className="mt-2 space-y-1.5">{a.action_items.map((it, i) => <li key={`ai-${i}`} className="flex items-start gap-2 text-sm text-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{it}</li>)}</ul></CardContent></Card>
              )}

              {a.phrase_matches?.length > 0 && (
                <Card className="border-border bg-card"><CardContent className="p-4"><p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Search className="h-4 w-4" />Watch-phrase mentions</p><div className="mt-2 space-y-2">{a.phrase_matches.map((m, i) => (<div key={`${m.phrase ?? "ph"}-${i}`} className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5"><div className="flex items-center gap-2"><Badge className="border border-amber-200 bg-amber-100 text-amber-800">{m.phrase}</Badge><span className="text-xs text-muted-foreground">{m.speaker}</span></div><p className="mt-1 text-sm text-foreground">“{m.snippet}”</p></div>))}</div></CardContent></Card>
              )}

              {a.highlights?.length > 0 && (
                <Card className="border-border bg-card"><CardContent className="p-4"><p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Quote className="h-4 w-4" />Highlights</p><div className="mt-2 space-y-3">{a.highlights.map((h, i) => (<div key={`hl-${i}-${h.speaker ?? ""}`} className="border-l-2 border-primary pl-3"><p className="text-sm italic text-foreground">“{h.quote}”</p><p className="mt-1 text-xs text-muted-foreground">{h.speaker}{h.reason ? ` — ${h.reason}` : ""}</p></div>))}</div></CardContent></Card>
              )}

              {a.speakers?.length > 0 && (
                <Card className="border-border bg-card"><CardContent className="p-4"><p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Users2 className="h-4 w-4" />Identified speakers</p><div className="mt-2 space-y-1.5">{a.speakers.map((s, i) => (<div key={i} className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{s.label}</span><span className="font-medium text-foreground">{s.guessed_name || "Unknown"}{s.role ? ` · ${s.role}` : ""}</span></div>))}</div></CardContent></Card>
              )}
            </>
          )}
        </div>
      </div>

      <SocialPostDialog open={showPost} onOpenChange={setShowPost} source={{ type: "recording", id, title: rec.title, public_url: null }} />
    </div>
  );
}
